
import * as React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

interface CheckboxProps extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  customColor?: string;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, customColor, ...props }, ref) => {
  // Create a darker shade for the border
  const getDarkerColor = (color: string) => {
    // Simple darkening by reducing opacity or using a darker variant
    // For hex colors, we'll use the color as-is but with reduced brightness
    return color.replace(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/, (_, r, g, b) => 
      `rgb(${Math.floor(Number(r) * 0.7)}, ${Math.floor(Number(g) * 0.7)}, ${Math.floor(Number(b) * 0.7)})`
    ).replace(/#([0-9a-f]{6})/i, (match) => {
      const r = parseInt(match.slice(1, 3), 16);
      const g = parseInt(match.slice(3, 5), 16);
      const b = parseInt(match.slice(5, 7), 16);
      return `#${Math.floor(r * 0.6).toString(16).padStart(2, '0')}${Math.floor(g * 0.6).toString(16).padStart(2, '0')}${Math.floor(b * 0.6).toString(16).padStart(2, '0')}`;
    });
  };

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "peer h-4 w-4 shrink-0 rounded-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        !customColor && "border border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
        className
      )}
      style={customColor ? { 
        border: `1px solid ${getDarkerColor(customColor)}`,
        backgroundColor: props.checked ? customColor : 'transparent',
        color: props.checked ? 'white' : 'transparent'
      } : undefined}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn("flex items-center justify-center text-current")}
      >
        <Check className="h-3.5 w-3.5" strokeWidth={3} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
})
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
